// Detalle de un reporte: su ficha y las columnas reales de su tabla (`GET /catalog/{table}`).
// No pinta filas de muestra: no se distinguirían del contenido real.
import type { ReactNode } from 'react';
import { Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { NoSource } from '@/shared/patterns/NoSource';
import { useTableColumns } from '../api/useReports';
import { CATEGORY_LABEL, JOB_STATUS_LABEL, PERIODICITY_CHIP, PERIODICITY_LABEL } from '../logic';
import type { ReportCard } from '../types';

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <>
            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </dt>
            <dd className="text-[11.5px]">{children}</dd>
        </>
    );
}

export function ReportDetailModal({
    card,
    onClose,
    onRun,
    onDownloadCsv,
    isRunning,
    isDownloading,
}: {
    card: ReportCard | null;
    onClose: () => void;
    onRun: () => void;
    onDownloadCsv: (jobId: string) => void;
    isRunning: boolean;
    isDownloading: boolean;
}) {
    const definition = card?.definition;
    const columns = useTableColumns(definition?.lakeTable ?? null);
    const lastRunIso = card?.lastRunAt ? new Date(card.lastRunAt).toISOString() : null;

    return (
        <Modal isOpen={card !== null} onClose={onClose} title={definition?.name ?? ''} size="lg">
            {card && definition ? (
                <div className="flex flex-col gap-4">
                    <div className="font-mono text-[10.5px] text-muted-foreground">
                        {/* eslint-disable-next-line no-restricted-syntax -- código de negocio del reporte, el que se cita en los documentos */}
                        {definition.reportId} · {definition.reference}
                    </div>

                    <dl className="grid grid-cols-[130px_1fr] gap-x-3 gap-y-1.5">
                        <Row label="Categoría">
                            <span
                                className={cn(
                                    'rounded px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.04em]',
                                    definition.category === 'REGULATORY'
                                        ? 'bg-warning-soft text-warning'
                                        : 'bg-muted text-foreground',
                                )}
                            >
                                {CATEGORY_LABEL[definition.category]}
                            </span>
                        </Row>
                        <Row label="Periodicidad">
                            <span
                                className={cn(
                                    'rounded-full px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.04em]',
                                    PERIODICITY_CHIP[definition.periodicity],
                                )}
                            >
                                {PERIODICITY_LABEL[definition.periodicity]}
                            </span>
                        </Row>
                        <Row label="Descripción">{definition.description}</Row>
                        <Row label="Referencia doc">
                            <span className="font-mono text-[10.5px]">{definition.reference}</span>
                        </Row>
                        <Row label="Última corrida">
                            {lastRunIso ? (
                                <>
                                    {formatDateMx(lastRunIso)} · {formatTimeMx(lastRunIso)}
                                    {card.lastRunStatus ? (
                                        <span className="ml-1.5 text-muted-foreground">
                                            · {JOB_STATUS_LABEL[card.lastRunStatus]}
                                        </span>
                                    ) : null}
                                </>
                            ) : (
                                'Nunca se ha corrido'
                            )}
                        </Row>
                        <Row label="Formatos">
                            <span className="rounded border border-border px-1.5 py-px font-mono text-[9.5px] font-semibold">
                                CSV
                            </span>
                            <span className="ml-2 text-[10.5px] text-muted-foreground">
                                El motor no genera PDF.
                            </span>
                        </Row>
                        <Row label="Volumen típico">
                            <NoSource reason="El motor no publica volumen ni tamaño estimado. El número de filas se conoce al correr el reporte." />
                        </Row>
                        {definition.regulatoryMandatory ? (
                            <Row label="Próximo vencimiento">
                                <NoSource reason="Ningún servicio publica el calendario regulatorio: el motor de reportes no conoce fechas de entrega." />
                            </Row>
                        ) : null}
                    </dl>

                    <div>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Columnas del archivo
                        </div>
                        {!definition.lakeTable ? (
                            <p className="rounded-md border border-dashed border-border p-2.5 text-[10.5px] leading-relaxed text-muted-foreground">
                                Este reporte todavía no tiene una tabla en el data lake, así que no
                                hay columnas que describir.
                            </p>
                        ) : columns.isPending ? (
                            <p className="text-[10.5px] text-muted-foreground">Consultando…</p>
                        ) : columns.data && columns.data.length > 0 ? (
                            <ul className="flex flex-wrap gap-1">
                                {[...columns.data]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((column) => (
                                        <li
                                            key={column.name}
                                            className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10.5px]"
                                            title={column.type}
                                        >
                                            {column.name}
                                        </li>
                                    ))}
                            </ul>
                        ) : (
                            <p className="rounded-md border border-dashed border-border p-2.5 text-[10.5px] leading-relaxed text-muted-foreground">
                                El motor no devolvió las columnas de esta tabla.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
                        <p className="mr-auto max-w-sm text-[10.5px] leading-snug text-muted-foreground">
                            {card.blockedReason ??
                                'La corrida queda en el historial; el archivo se baja aparte, en CSV.'}
                        </p>
                        <button
                            type="button"
                            disabled={card.blockedReason !== null || isRunning}
                            title={card.blockedReason ?? undefined}
                            onClick={onRun}
                            className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isRunning ? 'Generando…' : 'Generar ahora'}
                        </button>
                        <button
                            type="button"
                            disabled={card.downloadableJobId === null || isDownloading}
                            title={
                                card.downloadableJobId
                                    ? undefined
                                    : 'No hay ninguna corrida terminada de este reporte para descargar.'
                            }
                            onClick={() => {
                                if (card.downloadableJobId) onDownloadCsv(card.downloadableJobId);
                            }}
                            className="inline-flex h-8 items-center rounded-md border border-border px-3 text-[12px] font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isDownloading ? 'Descargando…' : 'Descargar CSV'}
                        </button>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
}
